
import * as React from "react"

export function useMediaQuery(query: string) {
  // Use ref to maintain stable value during renders but not trigger re-renders
  const matchesRef = React.useRef<boolean | undefined>(undefined);
  const [matches, setMatches] = React.useState<boolean | undefined>(undefined)
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  React.useEffect(() => {
    // Skip browser APIs during SSR
    if (typeof window === 'undefined') return;
    
    const mql = window.matchMedia(query)
    
    const onChange = () => {
      // Store immediate value in ref
      matchesRef.current = mql.matches;
      
      // Heavily debounce state updates that cause re-renders
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        // Only update state if value has changed and component is still mounted
        if (matchesRef.current !== matches) {
          setMatches(matchesRef.current);
        }
      }, 300) // Increased debounce time
    }
    
    // Use safer event listener pattern
    try {
      mql.addEventListener("change", onChange)
    } catch (e) {
      // Fallback for older browsers
      mql.addListener?.(onChange);
    }
    
    // Set initial value without waiting
    matchesRef.current = mql.matches;
    setMatches(mql.matches);
    
    return () => {
      // Clean up properly
      clearTimeout(timeoutRef.current);
      try {
        mql.removeEventListener("change", onChange);
      } catch (e) {
        mql.removeListener?.(onChange);
      }
    }
  }, [query])

  return matches
}

// Memoize the mobile state to prevent unnecessary re-renders
export function useIsMobile() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const lastValueRef = React.useRef<boolean | undefined>(undefined);
  
  // Stabilize the value - only return a new value if it's definitely changed
  if (isMobile !== undefined && isMobile !== lastValueRef.current) {
    lastValueRef.current = isMobile;
  }
  
  return lastValueRef.current ?? false;
}

// Cache of breakpoint values to minimize React state updates
const breakpointCache = new Map<string, boolean>();

export function useBreakpoint(breakpoint: string) {
  const breakpoints = {
    sm: '(max-width: 640px)',
    md: '(max-width: 768px)',
    lg: '(max-width: 1024px)',
    xl: '(max-width: 1280px)',
    '2xl': '(max-width: 1536px)',
  } as const;
  
  const query = breakpoints[breakpoint as keyof typeof breakpoints] || breakpoint;
  const matches = useMediaQuery(query);
  
  // Update cache when value changes
  React.useEffect(() => {
    if (matches !== undefined) {
      breakpointCache.set(query, matches);
    }
  }, [matches, query]);
  
  // Use cached value if current is undefined
  if (matches === undefined && breakpointCache.has(query)) {
    return breakpointCache.get(query);
  }
  
  return matches;
}
